import { useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, CheckCircle2, MapPin } from "lucide-react";

export default function InPersonTraining() {
  useSEO({
    title: "In-Person Boxing Training in New Jersey — Better Through Boxing",
    description: "1-on-1 in-person boxing training with Coach Chris Persaud across North and Central New Jersey. Mitts, footwork, sparring prep — limited availability, coach-vetted. Apply for in-person sessions.",
    path: "/in-person-training",
    keywords: ["in-person boxing training New Jersey", "private boxing coach NJ", "boxing lessons North Jersey", "boxing trainer Central Jersey", "1-on-1 boxing coaching", "boxing mitts training"],
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", zip: "", experience: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.zip || !form.experience) {
      return toast.error("Name, email, phone, zip, and experience level are required");
    }
    if (form.phone.replace(/\D/g, "").length < 7) {
      return toast.error("Please enter a valid phone number");
    }
    setSubmitting(true);
    try {
      const composedMessage = [
        `Zip: ${form.zip}`,
        `Experience: ${form.experience}`,
        form.message ? `\nLooking for:\n${form.message}` : "",
      ].filter(Boolean).join(" · ").replace(" · \n", "\n");
      await api.post("/leads/public", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        source: "In-Person Training (NJ)",
        message: composedMessage,
      });
      toast.success("Thanks! Coach Chris will reach out within 48 hours.");
      setForm({ name: "", email: "", phone: "", zip: "", experience: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App relative">
      <SiteHeader />
      <main data-testid="inperson-training-page">
        <section className="border-b border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">In-Person</span>
              <span className="text-[10px] uppercase tracking-widest bg-[#FF331F] text-white px-2 py-0.5"><MapPin className="h-3 w-3 inline-block mr-1" />NJ Only</span>
            </div>
            <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-6">
              Live in New Jersey?<br /><span className="text-[#FF331F]">Train in person.</span>
            </h1>
            <p className="text-zinc-300 leading-relaxed max-w-2xl text-base md:text-lg">
              Beyond online coaching, Coach Chris offers limited 1-on-1 in-person boxing training across North and Central New Jersey. Real mitts, real footwork on the floor, sparring prep when you&apos;re ready. Slots are limited and each request is coach-vetted personally.
            </p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-none mb-6">What you&apos;ll get.</h2>
            <ul className="space-y-3 text-sm text-zinc-300">
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Mitt work</strong> — direct hands-on coaching, real impact, real feedback.</span></li>
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Footwork on real floor</strong> — pivots, angles, lateral movement with proper traction.</span></li>
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Sparring preparation</strong> — defensive drills, light contact work, ring familiarity.</span></li>
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF331F] flex-shrink-0 mt-0.5" /><span><strong className="text-white">Beginner to competitor</strong> — coach calibrates intensity to where you actually are.</span></li>
            </ul>
            <div className="mt-8 border-l-2 border-[#FF331F] pl-4 text-sm text-zinc-400">
              Not in NJ? <Link to="/services" className="text-[#FF331F] hover:underline">Train virtually instead →</Link>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-[#0F0F0F] border border-[#27272A] p-8 space-y-4" data-testid="inperson-page-form">
            <h3 className="font-display uppercase text-xl tracking-widest mb-2">Request in-person training</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Full name *</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Athlete" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-page-name" /></div>
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Email *</label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-page-email" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Phone *</label><Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 123-4567" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-page-phone" /></div>
              <div className="space-y-1.5"><label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Zip code *</label><Input required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="07030" maxLength={10} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12 font-mono" data-testid="inperson-page-zip" /></div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Level of experience *</label>
              <select required value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="w-full h-12 bg-[#050505] border border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm px-3 text-sm text-white uppercase tracking-widest appearance-none cursor-pointer" data-testid="inperson-page-experience">
                <option value="" disabled className="bg-[#0F0F0F]">Select your level…</option>
                <option className="bg-[#0F0F0F]">Brand new — never trained</option>
                <option className="bg-[#0F0F0F]">Beginner — under 1 year</option>
                <option className="bg-[#0F0F0F]">Intermediate — 1-3 years</option>
                <option className="bg-[#0F0F0F]">Advanced — 3+ years</option>
                <option className="bg-[#0F0F0F]">Returning to boxing</option>
                <option className="bg-[#0F0F0F]">Active competitor</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">What are you looking for?</label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Goals, schedule, anything Coach Chris should know." rows={5} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="inperson-page-message" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6" data-testid="inperson-page-submit">
              {submitting ? "Sending…" : "Request in-person training"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-[11px] text-zinc-500 text-center">Coach Chris vets every request personally. Expect a reply within 48 hours.</p>
          </form>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
